import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { AdminNav } from '@/components/admin/AdminNav';

// Mirrors the server's JobResult (server/src/lib/sync-jobs.ts) — kept in sync
// by hand because the shared boundary forbids importing server types.
interface JobResult {
  ran: boolean;
  updated: number;
  skipped: number;
  reason?: string;
}

interface JobMeta {
  key: string;
  label: string;
  description: string;
  endpoint: string;
  /** sync-fixtures gets an extra full-reconcile button. */
  fullReconcile?: boolean;
}

const JOBS: JobMeta[] = [
  {
    key: 'sync-results',
    label: 'Sync results',
    description:
      'Pull live scores and final results from the upstream provider and mark finished matches.',
    endpoint: '/api/admin/jobs/sync-results',
  },
  {
    key: 'sync-lineups',
    label: 'Sync lineups',
    description: 'Pull starting XI and bench for upcoming/in-play matches from the provider.',
    endpoint: '/api/admin/jobs/sync-lineups',
  },
  {
    key: 'sync-fixtures',
    label: 'Sync fixtures',
    description:
      'Reconcile fixture schedule (kickoff times, statuses) against the provider. Standard run uses the rolling window; Full reconcile sweeps the whole tournament range.',
    endpoint: '/api/admin/jobs/sync-fixtures',
    fullReconcile: true,
  },
  {
    key: 'score-finished-matches',
    label: 'Score finished matches',
    description:
      'Safety-net pass that awards points for any finished match whose predictions are not yet scored.',
    endpoint: '/api/admin/jobs/score-finished-matches',
  },
];

export function AdminJobsPage() {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4 md:p-8">
      <AdminNav />
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Admin · Jobs</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">
          Manually trigger the background sync jobs. These also run automatically on a schedule —
          all jobs are idempotent and safe to re-run.
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {JOBS.map((j) => (
          <JobAction key={j.key} job={j} />
        ))}
      </div>
    </div>
  );
}

function JobAction({ job }: { job: JobMeta }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async (full: boolean) =>
      api.post<JobResult>(`${job.endpoint}${full ? '?full=true' : ''}`),
    onSuccess: (data) => {
      setError(null);
      setResult(formatResult(data));
      // Jobs may have changed match/scoring data server-side — drop the caches
      // the admin UI reads so it reflects the run.
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      void queryClient.invalidateQueries({ queryKey: ['matches'] });
      void queryClient.invalidateQueries({ queryKey: ['match'] });
      void queryClient.invalidateQueries({ queryKey: ['standings'] });
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiError ? err.message : 'Request failed');
    },
  });

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold">{job.label}</p>
        <p className="text-xs text-[color:var(--color-fg-muted)]">{job.description}</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => run.mutate(false)}
            disabled={run.isPending}
            className="inline-flex h-10 items-center justify-center rounded-xl bg-[color:var(--color-accent)] px-4 text-sm font-medium text-[color:var(--color-accent-fg)] disabled:pointer-events-none disabled:opacity-50"
          >
            {run.isPending ? 'Running…' : 'Run'}
          </button>
          {job.fullReconcile ? (
            <button
              type="button"
              onClick={() => run.mutate(true)}
              disabled={run.isPending}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-[color:var(--color-border)] px-4 text-sm font-medium text-[color:var(--color-fg)] hover:bg-[color:var(--color-bg)] disabled:pointer-events-none disabled:opacity-50"
            >
              Full reconcile
            </button>
          ) : null}
        </div>

        {result ? (
          <p className="text-right text-xs text-[color:var(--color-fg-muted)]">{result}</p>
        ) : null}
        {error ? (
          <p className="text-right text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </div>
    </div>
  );
}

function formatResult(data: JobResult): string {
  return data.ran
    ? `Updated ${data.updated}, skipped ${data.skipped}.`
    : `Did not run: ${data.reason ?? 'no reason'}.`;
}
